import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { hasValidAccessToken, useUserStore } from './stores/userStore';
import { useGroupStore } from './stores/groupStore';
import { MainLayout, AuthLayout } from '@ui/components/layout';
import { WebviewLayout } from './pages/(app-webview)/layout/WebviewLayout';
import { initializeApiClient } from './setup';
import { useGetMyGroupList } from '@hooks/generated/group';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import { RealtimeProvider } from './context/RealtimeContext';
import { ControlQueuePanel } from './ui/components/control/ControlQueuePanel';
import { ThemeProvider } from './context/ThemeContext';
import SettingsPage from './pages/(manager)/settings/page';
import BillingListPage from './pages/(manager)/settlement/page';

const LoginPage = React.lazy(() => import('./pages/(manager)/login/page'));
const RegisterPage = React.lazy(() => import('./pages/(manager)/register/page'));
const HomePage = React.lazy(() => import('./pages/(manager)/home/page'));
const SchedulesPage = React.lazy(() => import('./pages/(manager)/schedules/page'));
const ScheduleCreatePage = React.lazy(() => import('./pages/(manager)/schedules/new/page'));
const PetsPage = React.lazy(() => import('./pages/(manager)/pets/page'));
const NotificationsPage = React.lazy(() => import('./pages/(manager)/notifications/page'));
const ChatPage = React.lazy(() => import('./pages/(manager)/chat/page'));

const ManagerDashboardPage = React.lazy(() => import('./pages/(manager)/dashboard/page'));
const UserListPage = React.lazy(() => import('./pages/(manager)/users/page'));
const CheckInPage = React.lazy(() => import('./pages/(manager)/check-in/page'));
const HouseListPage = React.lazy(() => import('./pages/(manager)/houses/page'));
const AnalyticsPage = React.lazy(() => import('./pages/(manager)/analytics/page'));
const ReportPage = React.lazy(() => import('./pages/(manager)/report/page'));

const ConsentSignPage = React.lazy(() => import('./pages/(public)/consent/page'));
const ReportViewPage = React.lazy(() => import('./pages/(public)/report/page'));

const WebviewLoginPage = React.lazy(() => import('./pages/(app-webview)/login/page'));
const WebviewHomePage = React.lazy(() => import('./pages/(app-webview)/home/page'));
const WebviewCheckFlowPage = React.lazy(() => import('./pages/(app-webview)/check-flow/page'));
const WebviewSchedulePage = React.lazy(() => import('./pages/(app-webview)/schedule/page'));
const WebviewScheduleDetailPage = React.lazy(() => import('./pages/(app-webview)/schedule/detail/page'));
const WebviewScheduleEditPage = React.lazy(() => import('./pages/(app-webview)/schedule/edit/page'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
    },
  },
});

initializeApiClient();

interface UserData {
  name?: string;
  profilePictureUrl?: string;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useUserStore(state => state.isAuthenticated);
  const hasValidToken = hasValidAccessToken();
  const logout = useUserStore((state) => state.logout);

  React.useEffect(() => {
    if (isAuthenticated && !hasValidToken) {
      logout();
    }
  }, [isAuthenticated, hasValidToken, logout]);

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!hasValidToken) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function WebviewProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useUserStore(state => state.isAuthenticated);
  const hasValidToken = hasValidAccessToken();
  const logout = useUserStore((state) => state.logout);

  React.useEffect(() => {
    if (isAuthenticated && !hasValidToken) {
      logout();
    }
  }, [isAuthenticated, hasValidToken, logout]);

  if (!isAuthenticated) return <Navigate to="/webview/login" replace />;
  if (!hasValidToken) return <Navigate to="/webview/login" replace />;
  return <>{children}</>;
}

function WebviewRootRedirect() {
  const isAuthenticated = useUserStore(state => state.isAuthenticated);
  const hasValidToken = hasValidAccessToken();
  const logout = useUserStore((state) => state.logout);

  React.useEffect(() => {
    if (isAuthenticated && !hasValidToken) {
      logout();
    }
  }, [isAuthenticated, hasValidToken, logout]);

  return <Navigate to={isAuthenticated ? '/webview/home' : '/webview/login'} replace />;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useUserStore(state => state.isAuthenticated);
  const hasValidToken = hasValidAccessToken();
  const logout = useUserStore((state) => state.logout);

  React.useEffect(() => {
  if (isAuthenticated && !hasValidToken) {
    logout();
  }
  }, [isAuthenticated, hasValidToken, logout]);

  if (isAuthenticated && hasValidToken) return <Navigate to="/" replace />;
  if (isAuthenticated && !hasValidToken) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AppLayout({ children }: { children: React.ReactNode }) {
  const user = useUserStore(state => state.user) as UserData | null;
  const logout = useUserStore(state => state.logout);
  const navigate = useNavigate();

  useDocumentTitle();

  const selectedGroup = useGroupStore(state => state.selectedGroup);
  const groups = useGroupStore(state => state.groups);
  const setGroups = useGroupStore(state => state.setGroups);
  const selectGroupById = useGroupStore(state => state.selectGroupById);

  const { data: groupsData } = useGetMyGroupList({ request: {} });

  React.useEffect(() => {
    if (groupsData && Array.isArray(groupsData) && groupsData.length > 0) {
      const groupList = groupsData as unknown as { id: number; name?: string }[];
      setGroups(groupList);
    }
  }, [groupsData, setGroups]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleGroupChange = (groupId: number) => {
    selectGroupById(groupId);
  };

  return (
    <MainLayout
      userName={user?.name}
      userImage={user?.profilePictureUrl}
      onLogout={handleLogout}
      groups={groups}
      selectedGroupId={selectedGroup?.id}
      onGroupChange={handleGroupChange}
    >
      {children}
    </MainLayout>
  );
}

function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-orange-500 border-t-transparent" />
        <p className="text-sm text-neutral-500">로딩 중...</p>
      </div>
    </div>
  );
}

export default function ManageApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <RealtimeProvider>
            <React.Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/login" element={<PublicRoute><AuthLayout><LoginPage /></AuthLayout></PublicRoute>} />
                <Route path="/register" element={<PublicRoute><AuthLayout><RegisterPage /></AuthLayout></PublicRoute>} />

                <Route path="/" element={<ProtectedRoute><AppLayout><HomePage /></AppLayout></ProtectedRoute>} />
                <Route path="/billing" element={<ProtectedRoute><AppLayout><BillingListPage /></AppLayout></ProtectedRoute>} />
                <Route path="/schedules" element={<ProtectedRoute><AppLayout><SchedulesPage /></AppLayout></ProtectedRoute>} />
                <Route path="/schedules/new" element={<ProtectedRoute><AppLayout><ScheduleCreatePage /></AppLayout></ProtectedRoute>} />
                <Route path="/pets" element={<ProtectedRoute><AppLayout><PetsPage /></AppLayout></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><AppLayout><SettingsPage /></AppLayout></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute><AppLayout><NotificationsPage /></AppLayout></ProtectedRoute>} />
                <Route path="/chat" element={<ProtectedRoute><AppLayout><ChatPage /></AppLayout></ProtectedRoute>} />

                <Route path="/manager" element={<ProtectedRoute><AppLayout><ManagerDashboardPage /></AppLayout></ProtectedRoute>} />
                <Route path="/users" element={<ProtectedRoute><AppLayout><UserListPage /></AppLayout></ProtectedRoute>} />
                <Route path="/manager/check-in" element={<ProtectedRoute><AppLayout><CheckInPage /></AppLayout></ProtectedRoute>} />
                <Route path="/houses" element={<ProtectedRoute><AppLayout><HouseListPage /></AppLayout></ProtectedRoute>} />
                <Route path="/houses/detail" element={<ProtectedRoute><AppLayout><HouseListPage /></AppLayout></ProtectedRoute>} />
                <Route path="/analytics" element={<ProtectedRoute><AppLayout><AnalyticsPage /></AppLayout></ProtectedRoute>} />
                <Route path="/report" element={<ProtectedRoute><AppLayout><ReportPage /></AppLayout></ProtectedRoute>} />

                <Route path="/sign-consent/:token" element={<ConsentSignPage />} />
                <Route path="/report/:token" element={<ReportViewPage />} />

                <Route path="/webview" element={<WebviewRootRedirect />} />
                <Route path="/webview/login" element={<WebviewLayout><WebviewLoginPage /></WebviewLayout>} />
                <Route path="/webview/home" element={<WebviewProtectedRoute><WebviewLayout><WebviewHomePage /></WebviewLayout></WebviewProtectedRoute>} />
                <Route path="/webview/check-flow" element={<WebviewProtectedRoute><WebviewLayout><WebviewCheckFlowPage /></WebviewLayout></WebviewProtectedRoute>} />
                <Route path="/webview/check-in" element={<Navigate to="/webview/check-flow" replace />} />
                <Route path="/webview/check-out" element={<Navigate to="/webview/check-flow" replace />} />
                <Route path="/webview/pet-check-in-out" element={<Navigate to="/webview/check-flow?source=pet" replace />} />
                <Route path="/webview/schedule" element={<WebviewProtectedRoute><WebviewLayout><WebviewSchedulePage /></WebviewLayout></WebviewProtectedRoute>} />
                <Route path="/webview/schedule/:id" element={<WebviewProtectedRoute><WebviewLayout><WebviewScheduleDetailPage /></WebviewLayout></WebviewProtectedRoute>} />
                <Route path="/webview/schedule/:id/edit" element={<WebviewProtectedRoute><WebviewLayout><WebviewScheduleEditPage /></WebviewLayout></WebviewProtectedRoute>} />

                <Route path="/b2c/*" element={<Navigate to="/" replace />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </React.Suspense>
            <ControlQueuePanel />
          </RealtimeProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
